import * as React from 'react';
import { FieldValues, UseFormSetValue } from 'react-hook-form';
import { cn } from '@restworld/utils-common';

import { Variable } from '@restworld/utility-types';

import Checkbox, { ICheckboxProps, CheckBoxVariantType } from './checkbox.basic';

import type { JSX } from 'react';

export const CheckboxGroup: React.FunctionComponent<ICheckboxGroupProps> = ({
  id: checkBoxId,
  options,
  isWrapList = true,
  value,
  mode = 'static',
  helperText,
  isOrientation,
  mainTitle,
  subTitle,
  wrapperStyles,
  isEditable = false,
  testId,
  className,
  variant,
  ...rest
}) => {
  return options?.length ? (
    <div className={cn('flex flex-col gap-4 w-full', className)}>
      {/* Restore master's group heading (dropped in migration): large, centered title + small subtitle,
          so modal multi-selects (e.g. "Come ti muovi") show a heading instead of a bare list. */}
      {mainTitle ? (
        typeof mainTitle === 'string' ? (
          <p className="text-lg sm:text-xl font-semibold text-center leading-7">{mainTitle}</p>
        ) : (
          mainTitle
        )
      ) : null}
      {subTitle ? <p className="text-sm font-normal text-neutral-500 text-center">{subTitle}</p> : null}
      <ul
        id="checkbox"
        data-cy={testId}
        className={cn(
          'flex gap-2 h-full md:max-h-[15rem] md:overflow-y-auto md:px-4 md:pt-1',
          isWrapList && 'md:flex-wrap',
          isOrientation === 'row' || isOrientation === 'horizontal' ? 'flex-row gap-4 flex-wrap' : 'flex-col',
          isEditable && '!px-0 md:max-h-fit',
          wrapperStyles
        )}
      >
        {options.map(({ key, label, id }) => (
          <li id={`${id}-${checkBoxId}`} key={key} className={cn('pb-2 pl-1', isEditable && '!pl-0')}>
            <Checkbox
              key={key}
              title={label}
              isChecked={value ? value.indexOf(id as never) !== -1 : undefined}
              mode={mode}
              id={id?.toString()}
              testId={`${key}-checkbox`}
              {...rest}
              // Column (vertical) lists render each option full-width; row/horizontal chip layouts
              // keep the default w-fit so they still wrap like tags.
              labelClassName={isOrientation === 'row' || isOrientation === 'horizontal' ? undefined : 'w-full'}
            />
          </li>
        ))}
      </ul>
      {helperText}
    </div>
  ) : null;
};
export default CheckboxGroup;

export interface ICheckboxGroupProps extends Omit<ICheckboxProps, 'value'> {
  options?: Variable[];
  value?: string[] | number[];
  isWrapList?: boolean;
  helperText?: React.ReactNode;
  color?: string;
  setValue?: UseFormSetValue<FieldValues>;
  isOrientation?: 'row' | 'column' | 'horizontal' | 'vertical';
  mainTitle?: string | JSX.Element;
  subTitle?: string;
  wrapperStyles?: string;
  isEditable?: boolean;
  className?: string;
  /** Visual variant (consumed at group level, not passed to individual checkboxes) */
  variant?: CheckBoxVariantType;
}
